import { ProcessPage, ProcessPageParams } from '@adapters/navigation/page';
import { RouteUrl } from '@adapters/navigation/route';
import { Injectable } from '@angular/core';
import { Bank } from '@modules/process/models/bank';
import { Account } from '@core/model/domain/account';
import { BehaviorSubject } from 'rxjs';
import { ValidCaseState } from '@core/model/domain/caseState';

type StoreVariableTypes = {
  USER_ID: string;
  OPEN_BANKING_USER_TOKEN: string;
  PROCESS_PAGE: ProcessPage;
  TERMS_VERSION: string;
  CGU_VERSION: string;
  FOUND_ACCOUNTS: Account[];
  SELECTED_CONNECTION_ID: string;
  SELECTED_BANK: Bank;
  TAGGING_INIT_DATA_IN_BASE64: string;
  PROCESS_PAGE_PARAMS: ProcessPageParams<ProcessPage>;
  PROCESS_PAGE_HISTORY: Array<ProcessPage>;
  CURRENT_ROUTE: RouteUrl;
  SHOULD_TAG_ERROR_PAGE: boolean;
  SHOULD_IGNORE_NEXT_POPSTATE: boolean;
  CASE_STATE: ValidCaseState;
  IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON: boolean;
};

type StoreVariableAsBehaviorSubject = {
  // Would be cool to  X | null in the same BehaviorSubject
  [Key in StoreVariableKeys]: BehaviorSubject<StoreVariableTypes[Key]> | BehaviorSubject<null>;
};

type StoreVariableKeys = keyof StoreVariableTypes;

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private storedVariables: StoreVariableAsBehaviorSubject = {
    OPEN_BANKING_USER_TOKEN: this.getBehaviourSubjectFromStorage('OPEN_BANKING_USER_TOKEN'),
    PROCESS_PAGE: this.getBehaviourSubjectFromStorage('PROCESS_PAGE'),
    USER_ID: this.getBehaviourSubjectFromStorage('USER_ID'),
    FOUND_ACCOUNTS: this.getBehaviourSubjectFromStorage('FOUND_ACCOUNTS'),
    SELECTED_CONNECTION_ID: this.getBehaviourSubjectFromStorage('SELECTED_CONNECTION_ID'),
    SELECTED_BANK: this.getBehaviourSubjectFromStorage('SELECTED_BANK'),
    PROCESS_PAGE_PARAMS: this.getBehaviourSubjectFromStorage('PROCESS_PAGE_PARAMS'),
    TERMS_VERSION: this.getBehaviourSubjectFromStorage('TERMS_VERSION'),
    CGU_VERSION: this.getBehaviourSubjectFromStorage('CGU_VERSION'),
    TAGGING_INIT_DATA_IN_BASE64: this.getBehaviourSubjectFromStorage('TAGGING_INIT_DATA_IN_BASE64'),
    PROCESS_PAGE_HISTORY: this.getBehaviourSubjectFromStorage('PROCESS_PAGE_HISTORY'),
    CURRENT_ROUTE: this.getBehaviourSubjectFromStorage('CURRENT_ROUTE'),
    SHOULD_TAG_ERROR_PAGE: this.getBehaviourSubjectFromStorage('SHOULD_TAG_ERROR_PAGE'),
    SHOULD_IGNORE_NEXT_POPSTATE: this.getBehaviourSubjectFromStorage('SHOULD_IGNORE_NEXT_POPSTATE'),
    CASE_STATE: this.getBehaviourSubjectFromStorage('CASE_STATE'),
    IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON: this.getBehaviourSubjectFromStorage(
      'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON'
    ),
  };

  setVariable<TKey extends StoreVariableKeys>(key: TKey, value: StoreVariableTypes[TKey]): void {
    const storage = this.getStorageFromKey(key);
    if (storage == 'localStorage' || storage == 'sessionStorage') {
      const selectedStorage = storage == 'localStorage' ? localStorage : sessionStorage;

      const storageKeyName = this.getStorageKeyNameFromKey(key);
      const stringifiedValue = JSON.stringify(value);
      selectedStorage.setItem(storageKeyName, stringifiedValue);
    }

    const subject = this.storedVariables[key];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    subject.next(value as any); // Had to do this because of the union of BehaviourSubject<X> & BehaviorSubject<null>
  }

  getVariable<TKey extends StoreVariableKeys>(key: TKey): StoreVariableTypes[TKey] | null {
    const value = this.storedVariables[key].value;
    return value;
  }

  getVariableOrThrow<TKey extends StoreVariableKeys>(key: TKey): StoreVariableTypes[TKey] {
    const variable = this.getVariable(key);
    if (variable == null) {
      throw new Error(`Could not retrieve ${key} because not defined.`);
    }

    return variable;
  }

  reset() {
    for (const key in this.storedVariables) {
      this.clearStoreVariable(key as StoreVariableKeys);
    }
  }

  private getBehaviourSubjectFromStorage<TKey extends StoreVariableKeys>(
    key: TKey
  ): StoreVariableAsBehaviorSubject[TKey] {
    const storage = this.getStorageFromKey(key);
    if (storage == 'none') {
      return new BehaviorSubject(null);
    }

    const selectedStorage = storage == 'localStorage' ? localStorage : sessionStorage;

    const storageKeyName = this.getStorageKeyNameFromKey(key);
    const value = selectedStorage.getItem(storageKeyName);

    if (value == null) {
      return new BehaviorSubject(null);
    }

    const parsedValue = JSON.parse(value);
    return new BehaviorSubject(parsedValue);
  }

  private getStorageKeyNameFromKey(key: StoreVariableKeys): string {
    switch (key) {
      case 'OPEN_BANKING_USER_TOKEN':
        return 'open-banking-user-token';
      case 'FOUND_ACCOUNTS':
        return 'pobo-found-accounts';
      case 'SELECTED_CONNECTION_ID':
        return 'open-banking-selected-connection-id';
      case 'SELECTED_BANK':
        return 'pobo-selected-bank';
      case 'TAGGING_INIT_DATA_IN_BASE64':
        return 'open-banking-bank-tagging-init-data';
      case 'PROCESS_PAGE_PARAMS':
        return 'open-banking-process-page-params';
      case 'USER_ID':
        return 'open-banking-userId';
      case 'PROCESS_PAGE':
        return 'pobo-process-page';
      case 'PROCESS_PAGE_HISTORY':
        return 'open-banking-process-page-history';
      case 'CURRENT_ROUTE':
        return 'open-banking-route-before-browser-navigation';
      case 'SHOULD_TAG_ERROR_PAGE':
        return 'open-banking-should-tag-error-page';
      case 'SHOULD_IGNORE_NEXT_POPSTATE':
        return 'open-banking-ignore-next-popstate';
      case 'CASE_STATE':
        return 'open-banking-selected-accounts-case-state';
      case 'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON':
        return 'open-banking-is-popstate-triggered-via-cancel';
      case 'CGU_VERSION':
      case 'TERMS_VERSION':
        return 'NOT_PROVIDED';
      default:
        return this.caseSanitizer(key);
    }
  }

  private getStorageFromKey(key: StoreVariableKeys): 'localStorage' | 'sessionStorage' | 'none' {
    switch (key) {
      case 'TAGGING_INIT_DATA_IN_BASE64':
      case 'OPEN_BANKING_USER_TOKEN':
      case 'FOUND_ACCOUNTS':
      case 'SELECTED_CONNECTION_ID':
      case 'SELECTED_BANK':
      case 'PROCESS_PAGE_PARAMS':
      case 'USER_ID':
      case 'PROCESS_PAGE':
      case 'PROCESS_PAGE_HISTORY':
      case 'CURRENT_ROUTE':
      case 'SHOULD_TAG_ERROR_PAGE':
      case 'SHOULD_IGNORE_NEXT_POPSTATE':
      case 'CASE_STATE':
      case 'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON':
        return 'sessionStorage';

      case 'CGU_VERSION':
      case 'TERMS_VERSION':
        return 'none';
      default:
        return this.caseSanitizer(key);
    }
  }

  private clearStoreVariable<TKey extends StoreVariableKeys>(key: TKey): void {
    const storage = this.getStorageFromKey(key);
    if (storage == 'sessionStorage') {
      sessionStorage.removeItem(this.getStorageKeyNameFromKey(key));
    } else if (storage == 'localStorage') {
      localStorage.removeItem(this.getStorageKeyNameFromKey(key));
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.storedVariables[key].next(null as any); // Had to do this because of the union of BehaviourSubject<X> & BehaviorSubject<null>
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private caseSanitizer(_input: 'TERMS_VERSION' | 'CGU_VERSION'): never {
    throw new Error('Should never go here');
  }
}
